import { useSearchParams } from "react-router-dom";
import { Mail, MapPin } from "lucide-react";
import GroupHeader from "@/components/GroupHeader";
import GroupFooter from "@/components/GroupFooter";
import SEO from "@/components/SEO";
import InquiryForm from "@/components/site/InquiryForm";
import { InstagramLink } from "@/components/site/WalkTheLand";
import { Eyebrow, OutLink, Reveal } from "@/components/site/Bits";
import { INQUIRY_TYPES, useSettings } from "@/lib/site";

export default function Contact() {
    const s = useSettings();
    const [params] = useSearchParams();
    const preset = INQUIRY_TYPES.find((t) => t.key === params.get("type"));

    return (
        <div data-testid="contact-page" className="bg-cream text-ink min-h-screen">
            <SEO path="/contact" og="contact" title="Contact — Work with Verde 2050"
                 description="Invest alongside us, sell land, find land in Portugal, discuss a development project, or ask about development financing. Get in touch with Verde 2050." />
            <GroupHeader alwaysDark />
            <section className="bg-forest text-cream pt-36 md:pt-44 pb-16 md:pb-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal><Eyebrow dark>Contact</Eyebrow></Reveal>
                    <Reveal delay={100}>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight mt-8 max-w-4xl">
                            Have a plot, project or opportunity in mind? <em className="italic text-sage">We'd love to hear about it.</em>
                        </h1>
                    </Reveal>
                </div>
            </section>
            <section className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14">
                    <Reveal className="lg:col-span-8 order-2 lg:order-1">
                        <InquiryForm key={preset?.key || "default"} defaultType={preset?.value || "General inquiry"} />
                    </Reveal>
                    <Reveal delay={150} className="lg:col-span-4 order-1 lg:order-2 space-y-10">
                        <div>
                            <p className="mono-label text-moss mb-4">Direct</p>
                            <a href={`mailto:${s.contact_email}`} data-testid="contact-email" className="inline-flex items-center gap-3 text-lg text-forest hover:text-moss transition-colors">
                                <Mail className="h-5 w-5" /> {s.contact_email}
                            </a>
                            <p className="mt-4 inline-flex items-center gap-3 text-muted_text"><MapPin className="h-5 w-5" /> Greater Lisbon, Portugal</p>
                        </div>
                        <div>
                            <p className="mono-label text-moss mb-4">Follow the walks</p>
                            <InstagramLink dark={false} testid="contact-instagram" />
                        </div>
                        <div className="rounded-2xl bg-surface p-7">
                            <p className="mono-label text-moss mb-3">Development financing</p>
                            <p className="text-sm text-muted_text leading-relaxed mb-4">Financing requests are handled by Verde Capital, a separate business co-founded by Pieter.</p>
                            <OutLink href={s.verde_capital_url} event="verde_capital_click" label="contact-sidebar" testid="contact-verde-capital"
                                     className="text-sm font-medium text-forest border-b border-forest/40 pb-1 hover:text-moss">Go to Verde Capital</OutLink>
                        </div>
                    </Reveal>
                </div>
            </section>
            <GroupFooter />
        </div>
    );
}
